/**
 * FÄLTET. MIN EXTRAPOLATION — se komponenter.css avsnitt 19.
 *
 * Prototypen är en klickgenomgång och har inte ett enda formulärfält, så
 * formen nedan är byggd av token men INTE godkänd av ägaren. Den bör granskas.
 *
 * ==========================================================================
 * DEN ENDA REGEL SOM ÄR VIKTIG HÄR: ANVÄNDAREN SKRIVER ALDRIG EN KONTRAKTSENHET.
 *
 * shared/types.ts är absolut om enheterna — minuter, ören, meter och
 * tusendelar, allt som HELTAL i minsta enhet:
 *
 *     7,5 h      = 450 minuter
 *     1 295 kr   = 129500 ören
 *     4,5 mil    = 45000 meter
 *     2,5 m2     = 2500 tusendelar
 *
 * Montören skriver "7,5". Klienten räknar om. Fältet nedan är därför ett
 * TEXTFÄLT med inputMode 'decimal' och inte ett <input type="number">:
 *   - type=number ger olika decimaltecken i olika länder och tappar tyst ett
 *     komma i svensk tangentbordslayout
 *   - det ger dessutom små steg-pilar som är omöjliga att träffa med handskar
 *   - och ett tal som "0,5" blir 0.5 i en float, medan kontraktet vill ha 30
 *
 * Omräkningen sker med <Talfalt> nedan, som lämnar både texten användaren
 * skrev och det HELTAL som ska sparas. Skriv aldrig en egen parseFloat.
 * ==========================================================================
 */

import { useId } from 'react';
import type { InputHTMLAttributes, ReactNode, SelectHTMLAttributes, TextareaHTMLAttributes } from 'react';
import { tillHeltal } from './tal';

export interface Faltholjeprops {
  readonly etikett: ReactNode;
  /** Dämpad förklaring under fältet. */
  readonly hjalp?: ReactNode;
  /**
   * Felmeddelande. Kommer det från servern är det ERRORS[code].message_sv —
   * skriv aldrig en egen text för ett fel kontraktet redan namngivit.
   *
   * `fel` BETYDER "DET HÄR GÅR INTE ATT SPARA": tomt, minus, över taket. Fältet
   * får aria-invalid och röd ram. Ett värde som är fullt giltigt men värt en
   * varning — milpriset 0, till exempel, som skärmen själv ber om — hör hemma i
   * `varning` nedan. Ett giltigt värde som påstås vara fel lämnar användaren
   * utan väg framåt.
   */
  readonly fel?: ReactNode;
  /**
   * Följden av ett giltigt val, skriven i klartext. Ingen aria-invalid, ingen
   * röd ram — men syns, i --warn, under fältet. Står både `fel` och `varning`
   * visas bara felet: det ena stoppar sparningen, det andra gör det inte.
   */
  readonly varning?: ReactNode;
  readonly children: (id: string, ogiltigt: boolean) => ReactNode;
}

/** Etikett, kontroll, hjälptext, varning och fel. Alla fält nedan använder den. */
export const Faltholje = ({ etikett, hjalp, fel, varning, children }: Faltholjeprops) => {
  const id = useId();
  const ogiltigt = fel !== undefined && fel !== null && fel !== false;
  const varnar = !ogiltigt && varning !== undefined && varning !== null && varning !== false;
  return (
    <label className="k-falt" htmlFor={id}>
      <span className="k-etikett">{etikett}</span>
      {children(id, ogiltigt)}
      {hjalp !== undefined && <span className="k-hjalp">{hjalp}</span>}
      {varnar && <span className="k-varning">{varning}</span>}
      {ogiltigt && <span className="k-fel">{fel}</span>}
    </label>
  );
};

/* == Textfält ============================================================ */

export interface Faltprops
  extends Omit<InputHTMLAttributes<HTMLInputElement>, 'className' | 'id' | 'value' | 'onChange'> {
  readonly etikett: ReactNode;
  readonly varde: string;
  readonly onAndra: (varde: string) => void;
  readonly hjalp?: ReactNode;
  readonly fel?: ReactNode;
  /** Se Faltholjeprops.varning — giltigt värde, men värt att säga ifrån om. */
  readonly varning?: ReactNode;
}

export const Falt = ({ etikett, varde, onAndra, hjalp, fel, varning, ...ovrigt }: Faltprops) => (
  <Faltholje
    etikett={etikett}
    {...(hjalp === undefined ? {} : { hjalp })}
    {...(fel === undefined ? {} : { fel })}
    {...(varning === undefined ? {} : { varning })}
  >
    {(id, ogiltigt) => (
      <input
        id={id}
        className="k-kontroll"
        value={varde}
        onChange={(e) => onAndra(e.target.value)}
        aria-invalid={ogiltigt}
        {...ovrigt}
      />
    )}
  </Faltholje>
);

/* == Talfältet ===========================================================
   SJÄLVA OMRÄKNINGEN LIGGER I ui/tal.ts och inte här. Den är den enda kod i
   klienten där ett fel blir kronor på någons faktura, och en fil UTAN JSX går
   att köra och pröva rakt av under Node — test/klienten.ts räknar igenom varje
   regel i den. Raden nedan speglar den vidare, så att ingen import ändras.
   ======================================================================== */

export { FAKTOR, franHeltal, tillHeltal } from './tal';

export interface Talfaltprops extends Omit<Faltprops, 'onAndra' | 'inputMode'> {
  /** Se FAKTOR ovan. */
  readonly faktor: number;
  /**
   * Både texten och heltalet. Heltalet är null när texten inte är ett tal —
   * spara ALDRIG en rad då; visa i stället ett fel eller låt knappen vara av.
   */
  readonly onAndra: (varde: string, heltal: number | null) => void;
  /** Enheten i fältets högerkant: 'h', 'kr', 'mil', 'm2'. */
  readonly suffix?: string;
}

export const Talfalt = ({
  etikett,
  varde,
  onAndra,
  faktor,
  suffix,
  hjalp,
  fel,
  varning,
  ...ovrigt
}: Talfaltprops) => (
  <Faltholje
    etikett={etikett}
    {...(hjalp === undefined ? {} : { hjalp })}
    {...(fel === undefined ? {} : { fel })}
    {...(varning === undefined ? {} : { varning })}
  >
    {(id, ogiltigt) => (
      <span className={suffix === undefined ? '' : 'k-medsuffix'}>
        <input
          id={id}
          className="k-kontroll k-tal"
          type="text"
          inputMode="decimal"
          autoComplete="off"
          value={varde}
          onChange={(e) => onAndra(e.target.value, tillHeltal(e.target.value, faktor))}
          aria-invalid={ogiltigt}
          {...ovrigt}
        />
        {suffix !== undefined && <span className="k-suffix">{suffix}</span>}
      </span>
    )}
  </Faltholje>
);

/* == Valfältet =========================================================== */

export interface Valfaltprops<T extends string>
  extends Omit<SelectHTMLAttributes<HTMLSelectElement>, 'className' | 'id' | 'value' | 'onChange'> {
  readonly etikett: ReactNode;
  readonly varde: T;
  readonly onAndra: (varde: T) => void;
  /** Skicka kontraktets egen konstant när det finns en (ALL_ROLES m.fl.). */
  readonly val: readonly { readonly varde: T; readonly etikett: string }[];
  readonly hjalp?: ReactNode;
  readonly fel?: ReactNode;
}

export const Valfalt = <T extends string>({
  etikett,
  varde,
  onAndra,
  val,
  hjalp,
  fel,
  ...ovrigt
}: Valfaltprops<T>) => (
  <Faltholje etikett={etikett} {...(hjalp === undefined ? {} : { hjalp })} {...(fel === undefined ? {} : { fel })}>
    {(id, ogiltigt) => (
      <select
        id={id}
        className="k-kontroll"
        value={varde}
        onChange={(e) => onAndra(e.target.value as T)}
        aria-invalid={ogiltigt}
        {...ovrigt}
      >
        {val.map((v) => (
          <option key={v.varde} value={v.varde}>
            {v.etikett}
          </option>
        ))}
      </select>
    )}
  </Faltholje>
);

/* == Textytan ============================================================ */

export interface Textyteprops
  extends Omit<
    TextareaHTMLAttributes<HTMLTextAreaElement>,
    'className' | 'id' | 'value' | 'onChange'
  > {
  readonly etikett: ReactNode;
  readonly varde: string;
  readonly onAndra: (varde: string) => void;
  readonly hjalp?: ReactNode;
  readonly fel?: ReactNode;
}

export const Textyta = ({ etikett, varde, onAndra, hjalp, fel, ...ovrigt }: Textyteprops) => (
  <Faltholje etikett={etikett} {...(hjalp === undefined ? {} : { hjalp })} {...(fel === undefined ? {} : { fel })}>
    {(id, ogiltigt) => (
      <textarea
        id={id}
        className="k-kontroll"
        value={varde}
        onChange={(e) => onAndra(e.target.value)}
        aria-invalid={ogiltigt}
        {...ovrigt}
      />
    )}
  </Faltholje>
);

/** Två fält sida vid sida. */
export const Faltpar = ({ children }: { readonly children: ReactNode }) => (
  <div className="k-faltpar">{children}</div>
);
